import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, Lock, Mail, User, Shield, AlertCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSession } from "@/context/SessionContext";
import { errMsg } from "@/lib/format";

const QUICK_ACCOUNTS = [
  { label: "Route39 Owner", email: "r39.owner+clerk_test@example.com", role: "Owner (All Branches)" },
  { label: "Garage Manager", email: "r39.manager+clerk_test@example.com", role: "Manager (HQ)" },
  { label: "Technician", email: "r39.tech+clerk_test@example.com", role: "Tech (HQ)" },
  { label: "Platform Admin", email: "support@route39.in", role: "Super Admin" },
];

export default function AuthPage({ mode = "sign-in" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { me, login, register } = useSession();

  const [tab, setTab] = useState(mode === "sign-up" ? "sign-up" : "sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // If already logged in, redirect to home
  useEffect(() => {
    if (me?.provisioned) {
      navigate("/", { replace: true });
    }
  }, [me, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (tab === "sign-in") {
        await login(email, password);
      } else {
        await register({ name, email, password, phone });
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickFill = (accEmail) => {
    setEmail(accEmail);
    setPassword("Garage39-Test-2026!");
    setError(null);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid={`${tab}-page`}>
      <div className="flex flex-col items-center justify-center px-4 py-10 sm:px-8">
        <div className="w-full max-w-md">
          {/* Brand header */}
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2.5">
              <div className="h-10 w-10 rounded-md bg-[#18191D] flex items-center justify-center font-display font-extrabold text-[hsl(var(--gold))] text-lg">
                39
              </div>
              <div className="text-left leading-tight">
                <div className="font-display font-bold text-2xl tracking-tight">Garage39</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">By Route39</div>
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">Automotive Garage & Fleet Management System</p>
          </div>

          {/* Form card */}
          <div className="bg-white rounded-xl border shadow-sm p-6 sm:p-8">
            {/* Tabs */}
            <div className="flex border-b mb-6">
              <button
                type="button"
                className={`flex-1 pb-3 text-sm font-semibold border-b-2 transition-colors ${
                  tab === "sign-in"
                    ? "border-[#18191D] text-[#18191D]"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => { setTab("sign-in"); setError(null); }}
              >
                Sign In
              </button>
              <button
                type="button"
                className={`flex-1 pb-3 text-sm font-semibold border-b-2 transition-colors ${
                  tab === "sign-up"
                    ? "border-[#18191D] text-[#18191D]"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => { setTab("sign-up"); setError(null); }}
              >
                Register
              </button>
            </div>

            {error && (
              <div className="mb-5 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {tab === "sign-up" && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="reg-name"
                        type="text"
                        placeholder="John Doe"
                        className="pl-9"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reg-phone">Phone (Optional)</Label>
                    <Input
                      id="reg-phone"
                      type="tel"
                      placeholder="+91 98765 43210"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                </>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="auth-email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="auth-email"
                    type="email"
                    placeholder="name@route39.in"
                    className="pl-9"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid={tab === "sign-in" ? "login-email-input" : "register-email-input"}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="auth-password">Password</Label>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="auth-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••••••"
                    className="pl-9 pr-9"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid={tab === "sign-in" ? "login-password-input" : "register-password-input"}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#18191D] hover:bg-[#2B2D33] text-white font-medium h-10 mt-2"
                disabled={submitting}
                data-testid={tab === "sign-in" ? "login-submit-button" : "register-submit-button"}
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {tab === "sign-in" ? "Signing In..." : "Creating Account..."}
                  </span>
                ) : (
                  tab === "sign-in" ? "Sign In" : "Create Account"
                )}
              </Button>
            </form>

            {/* Quick demo accounts */}
            {tab === "sign-in" && (
              <div className="mt-6 pt-5 border-t">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium mb-2.5">
                  <Shield className="h-3.5 w-3.5 text-gold" />
                  <span>Quick Login Presets (Click to autofill):</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {QUICK_ACCOUNTS.map((acc) => (
                    <button
                      key={acc.email}
                      type="button"
                      onClick={() => handleQuickFill(acc.email)}
                      className="p-2 text-left rounded-md border text-xs hover:border-[#18191D] hover:bg-slate-50 transition-all group"
                    >
                      <div className="font-semibold text-[#18191D] group-hover:text-gold transition-colors truncate">
                        {acc.label}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">{acc.role}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right hero banner */}
      <div className="hidden lg:flex relative overflow-hidden bg-[#18191D]">
        <img src="/img/workshop.jpg" alt="Vehicle workshop" className="absolute inset-0 h-full w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#18191D] via-[#18191D]/40 to-transparent" />
        <div className="relative mt-auto p-12 text-white max-w-xl">
          <div className="g39-label text-[hsl(var(--gold))] mb-3">Garage & Fleet Management</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            One Platform.<br />Every Vehicle.<br /><span className="text-[hsl(var(--gold))]">Complete Control.</span>
          </h1>
          <p className="mt-5 text-sm text-white/70 max-w-md">
            Workshops, EV service centers and fleets — job cards, approvals, inventory, procurement and billing in one controlled workflow.
          </p>
        </div>
      </div>
    </div>
  );
}
